// src/pages/bepalingen/detail/lid-detail.tsx

import React from "react";
import { IconContext } from "react-icons";
import { MdKeyboardArrowDown, MdKeyboardArrowLeft, MdKeyboardArrowUp } from "react-icons/md";
import styles from './bepaling-detail.module.scss'; // Reuse existing styles
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import apiService from "@/api-service";
import { Alert, Button, ButtonGroup, Col, Container, Form, Row } from "react-bootstrap";
import HeaderedList from "@/components/headered-list/headered-list";
import HeaderedContainer from "@/components/headered-container/headered-container";
import BestekFilter from "@/components/bestek-filter/bestek-filter";
import { BestekFilterParams, initialBestekFilterParams, mapFilterParamsToQueryParams } from "@/components/bestek-filter/bestek-filter-params";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import { Bestek } from "@/models/bestek";
import BestekkenList from "@/components/bestek-list/bestek-list";
import BepalingUsageChart from "@/components/charts/bepaling-usage/bepaling-usage-chart";

// Interface for lid detail
interface LidDetail {
    id: number;
    nummer: string;
    tekst: string;
    artikel: {
        id: number;
        nummer: string;
        titel: string;
        paragraaf: {
            id: number;
            nummer: string;
            titel: string;
            hoofdstuk: {
                id: number;
                nummer: string;
                titel: string;
            }
        }
    };
    full_nummer: string;
    formatted_description: string;
}

export default function LidDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();

    const [lid, setLid] = useState<LidDetail>();
    const [usageChartFilterParamsExpanded, setUsageChartFilterParamsExpanded] = useState<boolean>(false);
    const [initialUsageChartFilterParams, setInitialUsageChartFilterParams] = useState<BestekFilterParams>();
    const [usageChartFilterParams, setUsageChartFilterParams] = useState<BestekFilterParams>(initialBestekFilterParams);

    const [bestekListFilterParamsQuery, setBestekListFilterParamsQuery] = useState<any>({});
    const [bestekListBestekken, setBestekListBestekken] = useState<Bestek[]>();
    const [bestekListCount, setBestekListCount] = useState<number>(-1);
    const [bestekListPage, setBestekListPage] = useState<number>(1);
    const [bestekListLoading, setBestekListLoading] = useState<boolean>(true);

    const [isExporting, setIsExporting] = useState<boolean>(false);

    // Helper function to create full bepaling nummer (lid level: 01.01.01.01)
    const getFullNummer = (lid: LidDetail): string => {
        return lid.full_nummer || `${lid.artikel.paragraaf.hoofdstuk.nummer}.${lid.artikel.paragraaf.nummer}.${lid.artikel.nummer}.${lid.nummer}`;
    };

    useEffect(() => {
        if (!lid || lid.id !== parseInt(id ?? "0")) {
            // FIXED: Use new lid detail endpoint
            apiService.get(`bepaling/lid/${id}/`)
                .then((response) => {
                    console.log('Lid loaded:', response.data);
                    setLid(response.data);

                    if (!initialUsageChartFilterParams) {
                        // Create initial filter params WITHOUT date restrictions
                        const initialParams = Object.assign({ ... initialBestekFilterParams }, { 
                            // Remove any date filters to show ALL data
                            startdatum: undefined,
                            einddatum: undefined,
                        });
                        setInitialUsageChartFilterParams(initialParams);
                        handleUsageChartFilterParamsChange({ ... initialParams });
                    }
                }, console.error);
        }
    });

    // Updated useEffect for bestek list - now uses the artikel ID and lid nummer
    useEffect(() => {
        if (!lid?.artikel?.id) return;
        
        setBestekListLoading(true);
        console.log('Fetching bestekken for lid:', lid.id, 'artikel:', lid.artikel.id, 'lid nummer:', lid.nummer, 'with params:', bestekListFilterParamsQuery);
        
        // FIXED: Use artikel ID and pass lid nummer filter
        apiService.post<{ result: Bestek[], total_count: number }>('/bepaling/bestek-search/', {
            search_params: bestekListFilterParamsQuery,
            include_bepaling_usage_for: lid.artikel.id, // Use artikel ID
            lid_nummer: lid.nummer, // FIXED: Filter for this specific lid
            page: bestekListPage
        }).then((response) => {
            console.log('Bestekken loaded for lid:', response.data);
            setBestekListBestekken(response.data.result);
            setBestekListCount(response.data.total_count);
        }).catch((error) => {
            console.warn('Bestek search failed:', error.message);
            setBestekListBestekken([]);
            setBestekListCount(0);
        }).finally(() => setBestekListLoading(false));
    }, [bestekListFilterParamsQuery, bestekListPage, lid?.artikel?.id, lid?.nummer]);

    const handleUsageChartFilterParamsChange = (params?: BestekFilterParams) => {
        if (params) {
            setBestekListFilterParamsQuery(mapFilterParamsToQueryParams(params));
            setUsageChartFilterParams(params);
        }
    }

    const download = (format: 'csv' | 'xlsx'): void => {
        if (isExporting) {
            return;
        }

        setIsExporting(true);
        loader.show("Export voorbereiden...");

        downloadFromAPI(
            {
                method: 'post',
                path: 'bepaling/export/detail/',
                data: {
                    bepaling_id: lid?.artikel?.id,
                    lid_nummer: lid?.nummer,
                    search_params: usageChartFilterParams ? mapFilterParamsToQueryParams(usageChartFilterParams) : {},
                    format: format
                }
            },
            `gebruik_bepaling_${lid ? getFullNummer(lid).replace(/\./g, '_') : 'export'}.${format}`,
            {
                finally: () => {
                    loader.hide();
                    setIsExporting(false);
                }
            }
        )
    }

    // Info items for display
    const hoofdstuk_items = [
        { label: "Nummer:", value: lid?.artikel.paragraaf.hoofdstuk.nummer ?? "" },
        { label: "Titel:", value: lid?.artikel.paragraaf.hoofdstuk.titel ?? "" }
    ];

    const paragraaf_items = [
        { label: "Nummer:", value: lid?.artikel.paragraaf.nummer ?? "" },
        { label: "Titel:", value: lid?.artikel.paragraaf.titel ?? "" }
    ];

    const artikel_items = [
        { label: "Nummer:", value: lid?.artikel.nummer ?? "" },
        { label: "Titel:", value: lid?.artikel.titel ?? "" }
    ];

    // FIXED: Add lid items
    const lid_items = [
        { label: "Nummer:", value: lid?.nummer ?? "" },
        { label: "Tekst:", value: lid?.tekst ?? "" }
    ];

    const bestekFilterCollapseIcon = (): JSX.Element => {
        if (usageChartFilterParamsExpanded) {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowUp onClick={() => setUsageChartFilterParamsExpanded(false)} /></IconContext.Provider>
        } else {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowDown onClick={() => setUsageChartFilterParamsExpanded(true)} /></IconContext.Provider>
        }
    }

    const exportButtonComponent = (): JSX.Element => {
        return (
            <>
                <div className="me-2" style={{ fontSize: '12px', marginTop: '4px' }}>Exporteer: </div>
                <ButtonGroup size={"sm"}>
                    <Button variant="light" onClick={() => download('csv')} disabled={isExporting}>
                        CSV
                    </Button>
                    <Button variant="light" onClick={() => download('xlsx')} disabled={isExporting}>
                        Excel
                    </Button>
                </ButtonGroup>
            </>
        )
    }

    const extraBestekListColumns = (header: boolean, bestek?: Bestek): JSX.Element => {
        if (header) {
            return (
                <>
                    <th>Bepaling vermeldingen</th>
                </>
            )
        } else {
            return (
                <>
                    <td>{ bestek?.bepaling_usage?.vermeldingen || 0 }</td>
                </>
            )
        }
    }

    return (
        <>
            <div className={styles.header}>
                <IconContext.Provider value={{size: '40px'}}><MdKeyboardArrowLeft onClick={() => navigate(-1)} /></IconContext.Provider>
                <div className={styles.header_title}>
                    Bepaling {lid ? getFullNummer(lid) : ''} 
                </div>
            </div>

            <Container fluid className="pb-4">
                <Row className="mt-4" style={{ rowGap: '25px' }}>
                    {lid && (
                        <>
                            <Col sm={12} md={6}>
                                <HeaderedList title="Hoofdstuk" items={hoofdstuk_items} />
                                <HeaderedList title="Paragraaf" items={paragraaf_items} />
                            </Col>

                            <Col sm={12} md={6}>
                                <HeaderedList title="Artikel" items={artikel_items} />
                                <HeaderedList title="Lid" items={lid_items} />
                            </Col>

                            <Col lg={12}>
                                <HeaderedContainer title="Gebruik" customRightComponent={exportButtonComponent()}>
                                    <Container fluid>
                                        <Row className="mt-4">
                                            <Col>
                                                <HeaderedContainer title="Bestek filter" customRightComponent={bestekFilterCollapseIcon()}>
                                                    {usageChartFilterParamsExpanded && (
                                                        <div className="mt-3">
                                                            <BestekFilter 
                                                                stateContext="local" 
                                                                initialLocalParams={initialUsageChartFilterParams} 
                                                                onFilterButtonClick={p => handleUsageChartFilterParamsChange(p)} 
                                                                disableHoofdCodeFiltering="full" 
                                                            />
                                                        </div>
                                                    )}
                                                </HeaderedContainer>

                                                {/* FIXED: BepalingUsageChart component with lid filter */}
                                                {lid && (
                                                    <BepalingUsageChart 
                                                        bepalingID={lid.artikel.id} 
                                                        bestekFilterParams={usageChartFilterParams}
                                                        lidNummer={lid.nummer} // FIXED: Pass lid nummer to chart
                                                    />
                                                )}

                                                <div className="mt-4">
                                                    <HeaderedContainer title="Bestekken">
                                                        <BestekkenList 
                                                            openInModal
                                                            loading={bestekListLoading}
                                                            bestekken={bestekListBestekken ?? []}
                                                            page={bestekListPage}
                                                            onPageChange={setBestekListPage}
                                                            extraColumns={extraBestekListColumns}
                                                            totalCount={bestekListCount}
                                                        />
                                                    </HeaderedContainer>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Container>
                                </HeaderedContainer>
                            </Col>
                        </>
                    )}
                </Row>
            </Container>
        </>
    )
}